import React, { useMemo } from 'react';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Reusable compact data grid — FinancialReports-style (light header, dense rows).
 *
 * columns: { key, label, width?, minWidth?, align?, type?: 'text'|'ltr'|'phone'|'number', truncate?, render? }
 */
export default function DataGrid({
  columns = [],
  rows = [],
  rowKey = 'id',
  loading = false,
  emptyMessage = 'لا توجد بيانات',
  emptyIcon,
  toolbar,
  stats,
  mobileCard,
  onRowClick,
  maxHeight,
  className = '',
}) {
  const isMobile = useIsMobile();
  const showMobileCards = isMobile && (mobileCard || columns.length > 0);

  const colStyles = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      const style = {};
      if (col.width) style.width = typeof col.width === 'number' ? `${col.width}px` : col.width;
      if (col.minWidth) style.minWidth = typeof col.minWidth === 'number' ? `${col.minWidth}px` : col.minWidth;
      if (col.maxWidth) style.maxWidth = typeof col.maxWidth === 'number' ? `${col.maxWidth}px` : col.maxWidth;
      map[col.key] = style;
    });
    return map;
  }, [columns]);

  function cellAlign(col) {
    if (col.align) return col.align;
    if (col.type === 'ltr' || col.type === 'phone' || col.type === 'number') return 'left';
    return 'right';
  }

  function renderCell(col, row) {
    const raw = row[col.key];
    if (col.render) return col.render(raw, row);
    if (raw == null || raw === '') return '—';
    return String(raw);
  }

  function cellClass(col) {
    const parts = ['vi-dg__cell'];
    if (col.truncate) parts.push('vi-dg__cell--truncate');
    if (col.type === 'ltr' || col.type === 'phone') parts.push('vi-dg__cell--ltr');
    if (col.type === 'number') parts.push('vi-dg__cell--num');
    return parts.join(' ');
  }

  return (
    <div className={`vi-data-grid ${className}`.trim()}>
      {toolbar && (
        <div className="vi-dg__toolbar">
          {toolbar.search && (
            <div className="vi-dg__search">
              <span className="vi-dg__search-icon" aria-hidden>🔍</span>
              <input
                type="search"
                className="vi-dg__search-input"
                value={toolbar.search.value}
                onChange={(e) => toolbar.search.onChange(e.target.value)}
                placeholder={toolbar.search.placeholder || 'بحث...'}
                aria-label={toolbar.search.placeholder || 'بحث'}
              />
            </div>
          )}
          <div className="vi-dg__toolbar-meta">
            {toolbar.countLabel && (
              <span className="vi-dg__count">{toolbar.countLabel}</span>
            )}
            {toolbar.actions}
          </div>
        </div>
      )}

      {stats?.length > 0 && (
        <div className="vi-dg__stats" aria-live="polite">
          {stats.map((s) => (
            <div key={s.label} className={`vi-dg__stat${s.tone ? ` vi-dg__stat--${s.tone}` : ''}`}>
              <span className="vi-dg__stat-label">{s.label}</span>
              <span className="vi-dg__stat-value">{loading ? '—' : s.value}</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="vi-dg__loading">جاري التحميل...</div>
      ) : rows.length === 0 ? (
        <div className="vi-dg__empty">
          {emptyIcon && <span className="vi-dg__empty-icon" aria-hidden>{emptyIcon}</span>}
          <p>{emptyMessage}</p>
        </div>
      ) : showMobileCards ? (
        <div className="vi-dg__cards">
          {rows.map((row) => (
            <div
              key={row[rowKey]}
              className={`vi-dg__card${onRowClick ? ' vi-dg__card--clickable' : ''}`}
              onClick={() => onRowClick?.(row)}
              onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } } : undefined}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {typeof mobileCard === 'function'
                ? mobileCard(row)
                : columns.filter((c) => c.key !== '_actions').map((col) => (
                  <div key={col.key} className="vi-dg__card-row">
                    <span className="vi-dg__card-label">{col.label}</span>
                    <span className={cellClass(col)}>{renderCell(col, row)}</span>
                  </div>
                ))}
            </div>
          ))}
        </div>
      ) : (
        <div
          className="vi-dg__scroll"
          style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
        >
          <table className="vi-dg__table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="vi-dg__th"
                    style={{ ...colStyles[col.key], textAlign: cellAlign(col) }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row[rowKey]}
                  className={idx % 2 === 0 ? 'vi-dg__row' : 'vi-dg__row vi-dg__row--alt'}
                  onClick={() => onRowClick?.(row)}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cellClass(col)}
                      style={{ ...colStyles[col.key], textAlign: cellAlign(col) }}
                      title={col.truncate ? String(renderCell(col, row)) : undefined}
                    >
                      {renderCell(col, row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ active, activeLabel = 'نشط', inactiveLabel = 'غير نشط' }) {
  return (
    <span className={`vi-badge ${active ? 'vi-badge--success' : 'vi-badge--danger'}`}>
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

export function GridActions({ children }) {
  return <div className="vi-dg__actions">{children}</div>;
}

export function GridActionButton({ variant = 'ghost', onClick, children, title }) {
  return (
    <button
      type="button"
      className={`vi-dg__action vi-dg__action--${variant}`}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

/** Icon-only action — iq-academic stype-icon-btn style */
export function GridIconButton({ variant = 'edit', onClick, title, icon, size }) {
  return (
    <button
      type="button"
      className={`vi-icon-btn vi-icon-btn--${variant}${size === 'sm' ? ' vi-icon-btn--sm' : ''}`}
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      title={title}
      aria-label={title}
    >
      {icon}
    </button>
  );
}